<div class="vmt-section">
  <h3 class="section-heading">VM Template</h3>
  <p class="section-desc">
    Provision and test the template virtual machine for this class. Forwarding is blocked until
    the template is created and testing is marked as done (snapshot).
  </p>

  <!-- 2-column layout: info card (left) + action card (right) -->
  <div class="vmt-grid">

    <!-- ═══════════ LEFT: Template Info Card ═══════════ -->
    <app-template-info-card class="vmt-info-card" [data]="templateInfoCardData"
      [drawerContent]="installationDrawerContent"></app-template-info-card>

    <!-- ═══════════ RIGHT: Action Card ═══════════ -->
    <mat-card class="vmt-card vmt-action-card">
      <mat-card-header>
        <mat-card-title class="vmt-card-title-row">
          <mat-icon class="vmt-card-title-icon">dns</mat-icon>
          Provisioning Controls
        </mat-card-title>
        <mat-card-subtitle>Create, test, and confirm the template VM</mat-card-subtitle>
      </mat-card-header>

      <mat-card-content class="vmt-action-content">

        <!-- Loading initial status -->
        <div *ngIf="isLoadingStatus" class="vmt-action-body vmt-center">
          <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
          <span class="vmt-state-label">Loading status…</span>
        </div>

        <!-- Error banner (action errors) -->
        <div *ngIf="!isLoadingStatus && actionError" class="vmt-error-banner">
          <mat-icon>error_outline</mat-icon>
          <span>{{ actionError }}</span>
        </div>

        <!-- No VM subscription configured — top-level blocked state, takes priority over
             every vmState (including the reused-template SNAPSHOTTED states below: a class
             with no subscription of its own must never show "Confirm Class" just because
             another class's shared (CustomerId, TenantSecretId) snapshot record exists). -->
        <div *ngIf="!isLoadingStatus && !hasVmSubscriptions"
          class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon" style="color: #f57c00;">warning_amber</mat-icon>
          <p class="vmt-state-title">No VM subscription configured</p>
          <p class="vmt-state-sub">
            This class has no VM subscription assigned. Add a valid subscription with
            available core quota before creating the template VM.
          </p>
          <button mat-flat-button color="accent" (click)="goToVmSubscriptions.emit()">
            <mat-icon>dns</mat-icon>
            Go to VM Subscriptions
          </button>
        </div>

        <!-- NOT_STARTED (subscription present) -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'NOT_STARTED'"
          class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon">cloud_queue</mat-icon>
          <p class="vmt-state-title">No template VM yet</p>
          <p class="vmt-state-sub">Create the template VM to begin provisioning the class environment.</p>
          <button mat-flat-button color="primary" [disabled]="isActioning" (click)="createTemplate()">
            <mat-spinner *ngIf="isActioning" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isActioning">add_circle_outline</mat-icon>
            {{ isActioning ? 'Starting…' : 'Create Template VM' }}
          </button>
        </div>

        <!-- PENDING -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'PENDING'" class="vmt-action-body vmt-center">
          <mat-progress-spinner mode="indeterminate" diameter="44" color="accent"></mat-progress-spinner>
          <p class="vmt-state-title">Provisioning queued…</p>
          <p class="vmt-state-sub">The template VM is waiting to be picked up. Checking every 5 s.</p>
          <button mat-stroked-button color="warn" [disabled]="isActioning" (click)="cancelProvisioning()">
            <mat-icon>cancel</mat-icon> Cancel
          </button>
        </div>

        <!-- IN_PROGRESS -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'IN_PROGRESS'" class="vmt-action-body">
          <mat-progress-bar mode="indeterminate" class="vmt-progress-bar"></mat-progress-bar>
          <p class="vmt-state-title vmt-center-text">Provisioning in progress…</p>
          <p class="vmt-state-sub vmt-center-text">Checking every 5 s for status updates.</p>
          <div *ngIf="logs.length > 0" class="vmt-log-tail">
            <p class="vmt-log-line" *ngFor="let line of logs">{{ line }}</p>
          </div>
        </div>

        <!-- CREATED -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'CREATED'" class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon vmt-icon-success">check_circle</mat-icon>
          <p class="vmt-state-title">Template VM ready</p>
          <p class="vmt-state-sub">
            The template VM is provisioned. Test it, then click <strong>Testing Done</strong> to
            create a snapshot and confirm the class.
          </p>
          <div class="vmt-btn-row">
            <button mat-stroked-button color="primary" [disabled]="isLaunching || isActioning"
              (click)="launchTemplateVm()" matTooltip="Open a remote desktop session into the template VM">
              <mat-spinner *ngIf="isLaunching" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!isLaunching">open_in_new</mat-icon>
              {{ isLaunching ? 'Launching…' : 'Launch Template VM' }}
            </button>
            <button mat-flat-button color="primary" [disabled]="isActioning || isLaunching || !hasCloudSettings"
              [matTooltip]="hasCloudSettings ? '' : 'You need cloud-settings access to mark testing done'"
              (click)="markTestingDone()">
              <mat-spinner *ngIf="isActioning" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!isActioning">task_alt</mat-icon>
              {{ isActioning ? 'Snapshotting…' : 'Testing Done' }}
            </button>
          </div>
        </div>

        <!-- SNAPSHOTTING -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'SNAPSHOTTING'" class="vmt-action-body">
          <mat-progress-bar mode="indeterminate" class="vmt-progress-bar"></mat-progress-bar>
          <p class="vmt-state-title vmt-center-text">Creating snapshot…</p>
          <p class="vmt-state-sub vmt-center-text">
            The template VM is being powered off and snapshotted. This takes 2–10 minutes.
            You can safely navigate away — the snapshot will continue in the background.
          </p>
        </div>

        <!-- FAILED -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'FAILED'" class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon vmt-icon-error">cancel</mat-icon>
          <p class="vmt-state-title">Provisioning failed</p>
          <p class="vmt-state-sub">An error occurred during template VM provisioning.</p>
          <div *ngIf="logs.length > 0" class="vmt-log-tail vmt-log-error">
            <p class="vmt-log-line" *ngFor="let line of logs">{{ line }}</p>
          </div>
          <button mat-flat-button color="warn" [disabled]="isActioning" (click)="retryProvisioning()">
            <mat-icon>replay</mat-icon> Retry
          </button>
        </div>

        <!-- SNAPSHOTTED, reused template — the Azure snapshot exists for this customer/tenant
             (reused from another class), but this class itself hasn't been marked tested yet. -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'SNAPSHOTTED' && !isTemplateTestedForClass"
          class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon" style="color: #f57c00;">warning_amber</mat-icon>
          <p class="vmt-state-title">Template snapshot available</p>
          <p class="vmt-state-sub">
            This class is reusing an existing template snapshot for this customer. Click
            <strong>Confirm Class</strong> to finish setting up this class and unlock forwarding.
          </p>
          <div class="vmt-btn-row">
            <button mat-stroked-button color="primary" [disabled]="isLaunching" (click)="launchTemplateVm()"
              matTooltip="Open a remote desktop session into the template VM">
              <mat-spinner *ngIf="isLaunching" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!isLaunching">open_in_new</mat-icon>
              {{ isLaunching ? 'Launching…' : 'Launch Template VM' }}
            </button>
            <button mat-flat-button color="primary"
              [disabled]="isConfirmingReused || isLaunching || !hasCloudSettings"
              [matTooltip]="hasCloudSettings ? '' : 'You need cloud-settings access to confirm this class'"
              (click)="confirmReusedTemplate()">
              <mat-spinner *ngIf="isConfirmingReused" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!isConfirmingReused">task_alt</mat-icon>
              {{ isConfirmingReused ? 'Confirming…' : 'Confirm Class' }}
            </button>
          </div>
        </div>

        <!-- SNAPSHOTTED, this class confirmed -->
        <div *ngIf="!isLoadingStatus && hasVmSubscriptions && vmState === 'SNAPSHOTTED' && isTemplateTestedForClass"
          class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon vmt-icon-confirmed">verified</mat-icon>
          <p class="vmt-state-title">Snapshot created — class confirmed</p>
          <p class="vmt-state-sub">
            The template VM snapshot was created successfully. This class is confirmed and ready to be forwarded.
          </p>
          <button mat-stroked-button color="primary" [disabled]="isLaunching" (click)="launchTemplateVm()"
            matTooltip="Open a remote desktop session into the template VM">
            <mat-spinner *ngIf="isLaunching" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isLaunching">open_in_new</mat-icon>
            {{ isLaunching ? 'Launching…' : 'Launch Template VM' }}
          </button>
        </div>

      </mat-card-content>

      <!-- ── VM Connection Template attachment ──
           Per round-4 feedback: lives INSIDE the Provisioning Controls card as a
           footer row rather than as a separate bottom section. Visibility still
           gated by `isVmProvisioned` since pre-attaching saves no time and may
           confuse trainers. -->
      <ng-container *ngIf="isVmProvisioned">
        <mat-divider class="vmt-card-divider"></mat-divider>
        <mat-card-content class="vmt-conn-template-row">
          <app-vm-connection-template-attach
            [currentId]="attachedVmConnectionTemplateId"
            [disabled]="isSavingVmConnectionTemplate"
            contextLabel="Applied to every participant session in this class. Detach to use the global server defaults."
            (change)="onVmConnectionTemplateChange($event)">
          </app-vm-connection-template-attach>
        </mat-card-content>
      </ng-container>
    </mat-card>

  </div><!-- /vmt-grid -->

</div>